/**
 * Copyright (c) 2024 Spazious, Inc All Rights Reserved
 * Proprietary and confidential
 *
 * Written by Rubeniskov <ruben.lopez@spazious.com>, Fri Feb 16 2024
 */

// https://github.com/pmndrs/react-three-fiber/blob/e3ec6f0fbb529fa75293428bd2eb51c3555abbea/packages/fiber/src/core/utils.ts#L129

export enum EquCompare {
  Reference = 'reference',
  Shallow = 'shallow',
}

export type EquConfig = {
  /** Compare arrays by reference equality a === b (default), or by shallow equality */
  arrays?: EquCompare;
  /** Compare objects by reference equality a === b (default), or by shallow equality */
  objects?: EquCompare;
  /** If true the keys in both a and b must match 1:1 (default), if false a's keys must intersect b's */
  strict?: boolean;
};

const num = (a: any): a is number => typeof a === 'number';
const arr = (a: any) => Array.isArray(a);
const obj = (a: any) => a === Object(a) && !arr(a) && typeof a !== 'function';
const str = (a: any): a is string => typeof a === 'string';
const und = (a: any) => a === void 0;

/**
 * Compare two values for equality.
 *
 * ```ts
 * equal(1, 1); // true
 * equal(1, 2); // false
 * equal([1, 2, 3], [1, 2, 3]); // true
 * equal([1, 2, 3], [1, 2, 4]); // false
 * equal({ a: 1, b: 2 }, { a: 1, b: 2 }, { object: EquCompare.Shallow }); // true
 * equal({ a: 1, b: 2 }, { a: 1, b: 2 }, { object: EquCompare.Reference }); // false
 * ```
 */
export const equal = (
  a: any,
  b: any,
  { arrays = EquCompare.Shallow, objects = EquCompare.Reference, strict = true }: EquConfig = {}
) => {
  // Wrong type or one of the two undefined, doesn't match
  if (typeof a !== typeof b || !!a !== !!b) return false;
  // Atomic, just compare a against b
  if (str(a) || num(a)) return a === b;
  const isObj = obj(a);
  if (isObj && objects === EquCompare.Reference) return a === b;
  const isArr = arr(a);
  if (isArr && arrays === EquCompare.Reference) return a === b;
  // Array or Object, shallow compare first to see if it's a match
  if ((isArr || isObj) && a === b) return true;
  // Last resort, go through keys
  let i;
  // Check if a has all the keys of b
  for (i in a) if (!(i in b)) return false;
  // Check if values between keys match
  if (isObj && arrays === EquCompare.Shallow && objects === EquCompare.Shallow) {
    for (i in strict ? b : a)
      if (!equal(a[i], b[i], { strict, objects: EquCompare.Reference })) return false;
  } else {
    for (i in strict ? b : a) if (a[i] !== b[i]) return false;
  }
  // If i is undefined
  if (und(i)) {
    // If both arrays are empty we consider them equal
    if (isArr && a.length === 0 && b.length === 0) return true;
    // If both objects are empty we consider them equal
    if (isObj && Object.keys(a).length === 0 && Object.keys(b).length === 0) return true;
    // Otherwise match them by value
    if (a !== b) return false;
  }
  return true;
};
